<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item
              :to="{
                name: 'DashboardHome',
              }"
            >
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>我的主控台</b-breadcrumb-item>
            <b-breadcrumb-item active>{{ pageTitle }}</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-card>
      <div class="row">
        <h4 class="col-12 mb-4 font-weight-bold">
          {{ `${pageTitle}` }}
        </h4>
      </div>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <div
          class="
            col-12 col-xl-6
            d-flex
            align-items-start align-items-xl-center
            mb-2
          "
        >
          <b-form-select
            v-model="query.category"
            :disabled="categories.length == 1"
            :options="categories"
            @change="handlePageResetWithoutReload"
          />
        </div>
        <div
          class="
            col-12 col-xl-6
            d-flex
            align-items-start align-items-xl-center
            mb-2
          "
        >
          <b-input-group>
            <b-form-input
              v-on:keyup.enter="handlePageResetWithoutReload"
              v-model="query.keyword"
              placeholder="搜尋"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageResetWithoutReload">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>

      <b-table
        striped
        hover
        responsive
        :items="taskLogs"
        :fields="fields"
        :busy="isLoading"
        @sort-changed="handleSort"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template #cell(logs)="data">
          <b-button
            v-if="data.item.logs && logsToString(data.item.logs).length > 60"
            v-b-modal.modal-full-view
            class="flex-shrink-0 mr-2"
            variant=""
            @click="logForFullView = data.item.logs"
          >
            <span class="mdi mdi-arrow-expand-all"></span>
          </b-button>
          <span>{{ logsToString(data.item.logs) | hiddenString(60) }}</span>
        </template>
        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>
      </b-table>

      <CustomPagination
        :currentPage="query.page"
        :totalRows="total"
        :perPage="query.per_page"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>
    <b-modal id="modal-full-view" title="檢視完整 Log 內容">
      <JsonEditor
        height="400px"
        v-model="logForFullView"
      />
      <template #modal-footer="{ cancel }">
        <b-button
          variant="outline-danger"
          size="sm"
          class="float-right"
          @click="cancel()"
        >
          關閉
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import serverTaskLogApi from "@/apis/server-task-log";
import moment from "moment";
import * as consts from "@/consts";
import JsonEditor from "@/components/Dashboard/JsonEditor";
import _ from "lodash";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { paginationMixin } from "@/mixins/pagination";
import { mapGetters } from "vuex";
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";

export default {
  components: {
    JsonEditor,
    CustomPagination,
  },
  mixins: [paginationMixin],
  props: {
    taskType: {
      type: String,
      required: true,
      validator: (value) => ["MANUAL", "SCHEDULED"].includes(value),
    },
  },
  data() {
    return {
      isLoading: false,
      taskLogs: [],
      logForFullView: null,
      categories: [{ value: null, text: "請選擇" }],
      fields: [
        {
          key: "category",
          label: "類型",
        },
        {
          key: "name",
          label: "名稱",
        },
        {
          key: "status",
          label: "狀態",
          formatter: (value) => {
            return consts.SERVER_TASK_LOG_STATUSES[value] ?? "";
          },
        },
        {
          key: "logs",
          label: "詳細資訊",
          formatter: (value) => {
            const string =
              typeof value === "string" ? value : JSON.stringify(value);
            return this.$options.filters.hiddenString(string, 60);
          },
        },
        {
          key: "memo",
          label: "備忘",
          formatter: (value) => {
            if (value === null || value === undefined) return "";
            const string =
              typeof value === "string" ? value : JSON.stringify(value);
            return this.$options.filters.hiddenString(string, 60);
          },
        },
        {
          key: "created_at",
          label: "時間",
          sortable: true,
        },
      ],
      total: 0,
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        order_by: "desc",
        keyword: null,
        category: null,
      },
    };
  },
  computed: {
    ...mapGetters("route", ["routeQuery"]),
    pageTitle() {
      return this.taskType === "MANUAL" ? "手動任務狀態" : "系統執行紀錄";
    },
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrlWithoutReload(this.query, this.$store, this.$router);
      },
    },
    routeQuery() {
      if (!this.initialized) return;
      this.fetchTaskLogs();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  async mounted() {
    await this.fetchTaskLogCategories();
    await this.fetchTaskLogs();
    this.initialized = true;
  },
  methods: {
    async fetchTaskLogCategories() {
      try {
        const { data } = await serverTaskLogApi.getServerTaskLogCategories({
          task_type: this.taskType,
        });
        this.categories = [
          { value: null, text: "請選擇" },
          ...data.data.map((category) => {
            return { value: category, text: category };
          }),
        ];
      } catch (error) {
        console.log("");
      }
    },
    async fetchTaskLogs() {
      try {
        this.isLoading = true;
        const cleanedQuery = _.omitBy(this.query, _.isNil);
        const { data } = await serverTaskLogApi.getServerTaskLogs({
          ...cleanedQuery,
          task_type: this.taskType,
        });
        this.total = data.meta.total;
        this.taskLogs = data.data;
      } catch (error) {
        console.log("");
      }
      this.isLoading = false;
    },
    logsToString(logs) {
      if (logs === null || logs === undefined) return "";
      if (typeof logs === "string") return logs;
      return JSON.stringify(logs);
    },
    handleSort(ctx) {
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageResetWithoutReload();
    },
    formatDate(value) {
      if (value) {
        return moment(String(value)).format("YYYY-MM-DD HH:mm");
      }
    },
  },
};
</script>
